import React, {useState, useContext, useEffect} from 'react'
import {getStockYardSummary, genericGetAll, apiGenericUpdate} from '../generic/crud'
import {ENTITY_ID_RAW_PURCHASE, ENTITY_ID_STOCK_YARD_INVENTORY} from '../generic/Constants'
import {showToast, TOAST_SUCCESS, TOAST_FAILURE} from '../../utils/toast/Toast'
import BedRockContext from '../../BedRockContext'
import {formatDateTime} from '../../utils/Utils'

const formatQty = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '0.000'
    return Number(value).toFixed(3)
}

const formatDate = (dateStr) => {
    if (!dateStr) return '—'
    return formatDateTime(dateStr)
}

const DUMP_LABELS = {
    QUARRY_TO_STOCKYARD: { label: 'Stock In', badge: 'success', sign: '+' },
    STOCKYARD_TO_CRUSHER: { label: 'Stock Out', badge: 'danger', sign: '-' },
    QUARRY_TO_CRUSHER: { label: 'Direct to Crusher', badge: 'warning', sign: '' },
}

const SVG_PATTERNS = [
    (color) => (
        <div style={{ position: 'absolute', top: -20, right: -20, width: '160px', height: '160px', opacity: 0.12, pointerEvents: 'none' }}>
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                <path fill={color} d="M47.5,-57.2C59.9,-46.7,67.5,-30.5,71.2,-13.1C74.9,4.3,74.6,22.9,66.4,37.2C58.2,51.5,42,61.5,24.6,67.1C7.2,72.7,-11.4,73.9,-28.5,68.5C-45.6,63.1,-61.2,51.1,-70.1,35.1C-79,19.1,-81.2,-0.9,-75.8,-18.3C-70.4,-35.7,-57.4,-50.5,-42.5,-60.4C-27.6,-70.3,-10.8,-75.3,3.6,-79.6C18,-83.9,35.1,-67.7,47.5,-57.2Z" transform="translate(100 100)" />
            </svg>
        </div>
    ),
    (color) => (
        <div style={{ position: 'absolute', top: 0, right: 0, width: '100%', height: '100%', opacity: 0.1, pointerEvents: 'none', overflow: 'hidden' }}>
            <svg viewBox="0 0 400 200" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
                <path fill={color} d="M0,100 C80,150 120,50 200,100 C280,150 320,50 400,100 L400,200 L0,200 Z" />
                <path fill={color} opacity="0.5" d="M0,120 C60,160 140,80 200,120 C260,160 340,80 400,120 L400,200 L0,200 Z" />
            </svg>
        </div>
    ),
    (color) => (
        <div style={{ position: 'absolute', top: 0, right: 0, width: '100%', height: '100%', opacity: 0.1, pointerEvents: 'none', overflow: 'hidden' }}>
            <svg viewBox="0 0 200 200" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
                <polygon points="200,0 200,80 140,0" fill={color} />
                <polygon points="200,60 200,140 120,60" fill={color} opacity="0.6" />
                <polygon points="0,200 80,200 0,140" fill={color} opacity="0.5" />
            </svg>
        </div>
    ),
    (color) => (
        <div style={{ position: 'absolute', top: -30, right: -30, width: '120px', height: '120px', opacity: 0.15, pointerEvents: 'none' }}>
            <svg viewBox="0 0 200 200">
                <circle cx="100" cy="100" r="90" fill={color} />
            </svg>
        </div>
    ),
]

const CARD_COLORS = ['#009EF7', '#50CD89', '#FFC700', '#F1416C', '#7239EA', '#0BB783']

const StockCard = ({item, index, onUpdated}) => {
    const [editing, setEditing] = useState(false)
    const [editValue, setEditValue] = useState('')
    const [saving, setSaving] = useState(false)

    const color = CARD_COLORS[index % CARD_COLORS.length]
    const pattern = SVG_PATTERNS[index % SVG_PATTERNS.length]
    const quantity = item.quantity || 0
    const unitSymbol = item.unitSymbol || 'Ton'
    const name = item.inventoryItemName || 'Unknown Item'
    const isPositive = quantity > 0
    const textColor = isPositive ? 'success' : quantity < 0 ? 'danger' : 'primary'

    const handleEdit = () => {
        setEditValue(String(quantity))
        setEditing(true)
    }

    const handleCancel = () => {
        setEditing(false)
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            const updated = { ...item, quantity: parseFloat(editValue) || 0 }
            await apiGenericUpdate(ENTITY_ID_STOCK_YARD_INVENTORY, updated, item.id)
            showToast('Stock updated', TOAST_SUCCESS)
            setEditing(false)
            onUpdated()
        } catch (e) {
            showToast('Failed to update stock', TOAST_FAILURE)
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="col-md-4 mb-5">
            <div className="card shadow-sm" style={{ position: 'relative', overflow: 'hidden', borderRadius: '12px' }}>
                {pattern(color)}
                <div className="card-body p-6" style={{ position: 'relative', zIndex: 1 }}>
                    <div className="d-flex align-items-center justify-content-between mb-5">
                        <div className="d-flex align-items-center">
                            <div className="symbol symbol-45px me-3">
                                <div className="symbol-label" style={{ backgroundColor: color + '1A' }}>
                                    <i className="ki-duotone ki-package fs-2" style={{ color }}>
                                        <span className="path1"></span>
                                        <span className="path2"></span>
                                        <span className="path3"></span>
                                    </i>
                                </div>
                            </div>
                            <div>
                                <div className="fs-4 fw-bold text-gray-800">{name}</div>
                                <div className="text-gray-500 fs-7">Unit: {unitSymbol}</div>
                            </div>
                        </div>
                        {!editing && (
                            <button
                                className="btn btn-sm btn-icon btn-light-primary"
                                onClick={handleEdit}
                                title="Edit stock"
                            >
                                <i className="ki-duotone ki-pencil fs-6">
                                    <span className="path1"></span>
                                    <span className="path2"></span>
                                </i>
                            </button>
                        )}
                    </div>

                    {editing ? (
                        <div>
                            <div className="text-gray-500 fs-8 fw-semibold text-uppercase mb-2">Update Stock</div>
                            <div className="d-flex align-items-center gap-2">
                                <input
                                    type="number"
                                    step="0.001"
                                    className="form-control form-control-sm"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    autoFocus
                                    onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                                />
                                <span className="text-gray-500 fs-7">{unitSymbol}</span>
                                <button className="btn btn-sm btn-success btn-icon" onClick={handleSave} disabled={saving}>
                                    <i className="bi bi-check-lg"></i>
                                </button>
                                <button className="btn btn-sm btn-light btn-icon" onClick={handleCancel} disabled={saving}>
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="d-flex justify-content-between align-items-end">
                            <div>
                                <div className="text-gray-500 fs-8 fw-semibold text-uppercase mb-1">Current Stock</div>
                            </div>
                            <div className="text-end">
                                <span className={`fw-bolder fs-2x text-${textColor}`}>{formatQty(quantity)}</span>
                                <span className="text-gray-500 fs-7 ms-1">{unitSymbol}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const StockYardPage = () => {
    const myData = useContext(BedRockContext)
    const [loading, setLoading] = useState(true)
    const [stockData, setStockData] = useState([])
    const [purchases, setPurchases] = useState([])

    useEffect(() => {
        myData.setBaseData({toolBarButtons: []})
    }, [])

    const loadData = () => {
        setLoading(true)
        Promise.all([
            getStockYardSummary().catch(() => ({data: []})),
            genericGetAll(ENTITY_ID_RAW_PURCHASE, {
                sizePerPage: 50,
                sortBy: 'purchaseDate',
                sortOrder: 'desc',
            }).catch(() => ({data: {entities: []}}))
        ]).then(([summaryRes, purchaseRes]) => {
            setStockData(Array.isArray(summaryRes?.data) ? summaryRes.data : [])
            const allPurchases = purchaseRes?.data?.entities || []
            setPurchases(allPurchases.filter(p => p.dumpLocation && p.dumpLocation !== ''))
        }).finally(() => setLoading(false))
    }

    useEffect(() => { loadData() }, [])

    return (
        <>
            {loading ? (
                <div className="card">
                    <div className="card-body p-10 text-center">
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Loading stock yard data...
                    </div>
                </div>
            ) : stockData.length === 0 && purchases.length === 0 ? (
                <div className="card">
                    <div className="card-body p-10 text-center">
                        <i className="ki-duotone ki-package fs-3x text-gray-400 mb-5">
                            <span className="path1"></span>
                            <span className="path2"></span>
                            <span className="path3"></span>
                        </i>
                        <h4 className="text-gray-600 mb-2">No Stock Data</h4>
                        <p className="text-gray-500 fs-6">
                            Stock entries will appear here when purchases are made with Stock Yard dump locations.
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Header */}
                    <div className="card mb-5" style={{ position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', top: -20, right: -20, width: '200px', height: '200px', opacity: 0.06, pointerEvents: 'none' }}>
                            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                                <rect x="20" y="40" width="70" height="90" rx="8" fill="#009EF7" />
                                <rect x="100" y="60" width="70" height="70" rx="8" fill="#009EF7" />
                                <rect x="50" y="20" width="70" height="50" rx="8" fill="#009EF7" opacity="0.5" />
                                <rect x="30" y="140" width="140" height="15" rx="4" fill="#009EF7" />
                                <rect x="30" y="165" width="140" height="15" rx="4" fill="#009EF7" opacity="0.5" />
                            </svg>
                        </div>
                        <div className="card-body p-5" style={{ position: 'relative', zIndex: 1 }}>
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <h3 className="fw-bolder mb-1">Stock Yard Inventory</h3>
                                    <span className="text-gray-500 fs-6">{stockData.length} item(s) in stock yard</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Stock Cards */}
                    {stockData.length > 0 && (
                        <div className="row">
                            {stockData.map((item, index) => (
                                <StockCard
                                    key={item.inventoryItemId || index}
                                    item={item}
                                    index={index}
                                    onUpdated={loadData}
                                />
                            ))}
                        </div>
                    )}

                    {/* Recent Purchases affecting Stock */}
                    {purchases.length > 0 && (
                        <div className="card mb-5">
                            <div className="card-body p-6">
                                <div className="d-flex align-items-center mb-4">
                                    <div className="symbol symbol-35px me-3">
                                        <div className="symbol-label bg-light-info">
                                            <i className="ki-duotone ki-time fs-3 text-info">
                                                <span className="path1"></span>
                                                <span className="path2"></span>
                                            </i>
                                        </div>
                                    </div>
                                    <h5 className="fw-bold mb-0 text-info">Recent Purchases</h5>
                                    <span className="badge badge-light-dark ms-2">{purchases.length}</span>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-row-bordered table-row-gray-200 align-middle gs-3 gy-3">
                                        <thead>
                                            <tr className="fw-bold text-muted bg-light">
                                                <th className="ps-4 min-w-50px">#</th>
                                                <th className="min-w-80px">Ticket</th>
                                                <th className="min-w-100px">Date</th>
                                                <th className="min-w-120px">Supplier</th>
                                                <th className="min-w-120px">Item</th>
                                                <th className="text-end min-w-80px">Qty</th>
                                                <th className="text-center min-w-100px">Stock Impact</th>
                                                <th className="pe-4 min-w-80px">Vehicle</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {purchases.map((p, index) => {
                                                const dump = DUMP_LABELS[p.dumpLocation] || { label: p.dumpLocation, badge: 'secondary', sign: '' }
                                                const invItem = (p.itemsList || []).find(i => i.itemType === 'INVENTORY')
                                                const qty = invItem?.quantity || 0
                                                const unit = invItem?.unitSymbol || 'Ton'
                                                const isStockIn = p.dumpLocation === 'QUARRY_TO_STOCKYARD'
                                                const isStockOut = p.dumpLocation === 'STOCKYARD_TO_CRUSHER'

                                                return (
                                                    <tr key={p.id || index}>
                                                        <td className="ps-4 text-gray-600 fs-7">{index + 1}</td>
                                                        <td>
                                                            <span className="fw-bold text-primary">{p.purchaseTicketNumber || '—'}</span>
                                                        </td>
                                                        <td>
                                                            <span className="fw-semibold text-gray-800">{formatDate(p.purchaseDate)}</span>
                                                        </td>
                                                        <td>
                                                            <span className="text-gray-700">{p.supplier?.name || '—'}</span>
                                                        </td>
                                                        <td>
                                                            <span className="fw-semibold text-gray-800">{invItem?.inventoryItemName || '—'}</span>
                                                            <small className="text-gray-500 d-block fs-8">{unit}</small>
                                                        </td>
                                                        <td className="text-end">
                                                            <span className="fw-bold">{formatQty(qty)}</span>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className={`badge badge-light-${dump.badge} fw-bold`}>
                                                                {(isStockIn || isStockOut) && (
                                                                    <i className={`bi bi-arrow-${isStockIn ? 'up' : 'down'}-circle-fill me-1`}></i>
                                                                )}
                                                                {dump.label}
                                                            </span>
                                                        </td>
                                                        <td className="pe-4 text-gray-600 fs-7">
                                                            {p.vehicleNumber || '—'}
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    )
}

export {StockYardPage}
